import { z } from "zod";
//#region src/paging.ts
/**
* The house paging primitives (spine V-1).
*
* ⛔ **ONE contract, everywhere a collection read pages.** Request: an opaque
* keyset `cursor` and a bounded `limit`. Response: the items, a `nextCursor`,
* and (see {@link pagedEnvelopeSchema}) the truncation signal. Cursors are
* **keyset** — a stable sort key plus a tiebreaker id — and never offsets: an
* offset re-reads or skips rows the moment anything is inserted, which on an
* append-only audit trail is guaranteed rather than unlikely.
*
* ## ⛔ THE THREE RULES THAT ARE NOT NEGOTIABLE
*
* 1. **Tools VALIDATE a limit; they never clamp it.** An agent that asks for
*    5000 must be TOLD the maximum, not silently handed the maximum and left
*    believing it received everything. The clamp is the version that makes an
*    agent draw a conclusion from a truncated list. (The rule was written for
*    `getAppPinHistoryArgsSchema` at `app-read-tools.ts:117-125`; V-1 makes it
*    surface-wide.)
* 2. **The default protects the AGENT's context; the max protects the SERVER.**
*    They are different numbers for different reasons, so a surface may not
*    collapse them. Defaults are ≤ 100 (V-1).
* 3. **A cursor is opaque, bounded and carries NO authorization input.** Scope
*    is re-derived from the path parameter plus the principal's grants on every
*    page. A cursor is data the caller has already seen, so replaying one
*    obtained from another walk can at worst produce a wrong page — never a
*    page the caller may not read. `.max()` on the token is what stops an
*    unbounded string reaching the base64url decoder.
*
* The root barrel re-exports this module and browser bundles import that barrel
* through `@zion/sdk-react` (#3073), so the codec below uses only what Node 24 and
* every browser define: `TextEncoder`, `TextDecoder`, `btoa` and `atob`.
*/
/**
* Upper bound on an accepted cursor token for a NEW paged surface.
*
* ⚠️ Deliberately NOT shared with `HISTORY_CURSOR_MAX` (512) or
* `DEPLOYMENTS_CURSOR_MAX` (256) in `app-admin-inputs.ts`. Those are E11's and
* E2's own bounds and that file records the decision not to unify them; this is
* a third bound for a third set of surfaces, and it happens to equal E11's.
* ⛔ Do not "collapse the duplicate" — the equality is a coincidence of size,
* not a shared decision.
*/
const PAGE_CURSOR_MAX = 512;
/** The house default page size. ⛔ `≤ 100` is V-1's rule, not a suggestion. */
const PAGE_DEFAULT_LIMIT = 100;
const utf8Encoder = new TextEncoder();
/** `fatal` rejects bytes that are not UTF-8; `ignoreBOM` keeps a leading BOM so `JSON.parse` refuses it. */
const utf8Decoder = new TextDecoder("utf-8", {
	fatal: true,
	ignoreBOM: true
});
/** A base64url body with at most two trailing padding characters. `atob` checks the padding itself. */
const BASE64URL_TOKEN = /^[A-Za-z0-9_-]*={0,2}$/;
/** RFC 4648 section 5 base64url, unpadded: the same text Node's `'base64url'` encoding writes. */
function bytesToBase64Url(bytes) {
	let binary = "";
	for (const byte of bytes) binary += String.fromCharCode(byte);
	return btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}
/**
* The inverse of {@link bytesToBase64Url}, padded or not. Throws on a character
* outside the alphabet; `atob` throws on a length of 1 mod 4 and on wrong padding.
*/
function base64UrlToBytes(token) {
	if (!BASE64URL_TOKEN.test(token)) throw new SyntaxError("invalid base64url");
	const binary = atob(token.replace(/-/g, "+").replace(/_/g, "/"));
	return Uint8Array.from(binary, (char) => char.charCodeAt(0));
}
/** Thrown when a client-supplied keyset cursor cannot be decoded. */
var InvalidPageCursorError = class extends Error {
	token;
	constructor(token) {
		super("invalid cursor");
		this.token = token;
		this.name = "InvalidPageCursorError";
	}
};
/**
* Encode a keyset cursor payload as a URL-safe base64url token.
*
* ## The envelope is shared; the PAYLOAD KEYS are per-endpoint
*
* `base64url(JSON)` with short single-letter keys, decoded through a
* `.strict()` zod payload schema, and any malformation raised as a TYPED error
* the service maps to **400** — never a 500, never a silent first page. That is
* already the house codec in four places (`encodeHistoryCursor`,
* `encodeAdoptionCursor`, `encodeActivityCursor`,
* `BillingAdminService.decodeCursor`); this is the shared implementation of the
* ENVELOPE and the FAILURE MODE. Each endpoint still owns its payload keys.
*
* ⛔ **`message` is the fixed string `'invalid cursor'` and does NOT interpolate
* the token.** The token is caller-supplied and reaches a log line and,
* historically, a 400 body; echoing it back is a reflected-input surface for no
* diagnostic gain (the caller already has the token). `InvalidPageCursorError.token`
* carries it for the server-side log. ⚠️ This is a deliberate DIVERGENCE from
* `InvalidHistoryCursorError`, which interpolates; E11 is not re-worked here, and
* the divergence is recorded rather than propagated.
*/
function encodeKeysetCursor(payload) {
	return bytesToBase64Url(utf8Encoder.encode(JSON.stringify(payload)));
}
/**
* Decode a keyset cursor against its endpoint's payload schema.
*
* ⛔ **The schema argument is REQUIRED and must be `.strict()`.** A tampered
* token carrying extra keys, a non-JSON body, a non-base64url string and a
* payload of the wrong shape must all land on the SAME typed error, because the
* caller cannot distinguish them and a service that branches on them leaks how
* the token is built.
*
* The decode step rejects a character outside the base64url alphabet, a `=`
* anywhere but one or two trailing padding characters, a length of 1 mod 4, wrong
* padding, and bytes that are not UTF-8. It accepts a token with or without its
* trailing padding. The `JSON.parse` and the zod parse then reject a body that is
* not JSON or has the wrong shape. Every one of those lands on
* {@link InvalidPageCursorError}.
*/
function decodeKeysetCursor(schema, token) {
	let parsed;
	try {
		parsed = JSON.parse(utf8Decoder.decode(base64UrlToBytes(token)));
	} catch {
		throw new InvalidPageCursorError(token);
	}
	const result = schema.safeParse(parsed);
	if (!result.success) throw new InvalidPageCursorError(token);
	return result.data;
}
/**
* The V-1 request fragment, spread into an endpoint's own query schema.
*
* ⛔⛔ **`limit` IS `.optional()` HERE AND MUST NOT CARRY `.default()`.**
*
* This is the single most load-bearing detail in the whole workstream and it is
* the one a developer will "fix". The self-selecting envelope (V-1) branches on
* **whether the caller sent a paging param**. A `.default()` makes the parsed
* value always present, so the controller can no longer tell "absent" from
* "defaulted" — and every legacy caller silently receives the enveloped shape.
* That is the outage, not a style regression.
*
* ⇒ The DEFAULT is applied INSIDE the enveloped branch, by the service, from
* {@link PAGE_DEFAULT_LIMIT} or the endpoint's own constant.
*
* ⚠️ The **TOOL-side** schemas are the mirror image: they DO carry `.default()`,
* because an MCP tool always pages (V-1) and an absent limit there means "the
* agent did not choose", not "the agent wants the legacy shape". The asymmetry
* is deliberate and is asserted on both sides (§6.1 P-6, §6.4 T-3).
*
* ⛔⛔ **DECISIONS R-26 makes this UNIVERSAL, not upstream-only:** a `.default()`
* on a limit belongs ONLY in a tool-args schema — **never in a schema a legacy
* route caller reaches**, in-process routes included. `appPodsQuerySchema` is the
* case that proved it: it is bound at a controller the live Site-Admin UI calls
* AND spread into tool args, and defaulting it would have silently dropped that
* pane from up-to-500 pods to the default on an operator debugging surface. Route
* schemas spread {@link pagedQueryFields}; tool args spread
* {@link pagedToolFields} on top; the SERVICE applies the default in its paging
* branch and nowhere else.
*
* `z.coerce` on `limit` is required: a query string carries `"100"`, and an MCP
* client may JSON-encode a number as a string.
*/
function pagedQueryFields(opts) {
	return {
		/**
		* Opaque base64url keyset token from the previous page's `nextCursor`.
		*
		* ⛔ **The charset regex is not decoration** (security M-1). §2.2 recorded that
		* the original decoder silently DROPPED invalid characters instead of
		* rejecting them. {@link decodeKeysetCursor} now rejects them itself, and this
		* regex keeps the refusal at the schema: anything outside the base64url
		* alphabet is a 400 before it reaches the decoder, `JSON.parse` or a bound SQL
		* parameter.
		*
		* ⚠️ Query-string/path injection via a cursor is NOT reachable today —
		* `CpServiceClient` hands `params` to axios, which URL-encodes
		* (`cp-service.client.ts:343`), and `pathParams` are `encodeURIComponent`'d
		* with the two bags unable to cross (`:228-266`). This is defence in depth, and
		* it shrinks the accepted token space by ~99%.
		*/
		cursor: z.string().min(1).max(512).regex(/^[A-Za-z0-9_-]+$/).optional(),
		/** Page size. Bounded `[1, maxLimit]`. ⛔ NO `.default()` — see the docblock. */
		limit: z.coerce.number().int().min(1).max(opts.maxLimit).optional()
	};
}
/**
* The TOOL-side request fragment: the same two fields, with `limit` DEFAULTED.
*
* ⭐ A separate function rather than a boolean parameter on
* {@link pagedQueryFields}, deliberately. A boolean would let a route schema
* opt into the default with a one-character edit and no reviewer signal; two
* named functions make "which side am I on" a thing you have to write down.
*/
function pagedToolFields(opts) {
	return {
		/** ⛔ Same charset regex as `pagedQueryFields` — see its docblock (security M-1). */
		cursor: z.string().min(1).max(512).regex(/^[A-Za-z0-9_-]+$/).optional(),
		limit: z.coerce.number().int().min(1).max(opts.maxLimit).default(opts.defaultLimit ?? 100)
	};
}
/**
* ⛔⛔ **`truncated` HAS ONE MEANING, SURFACE-WIDE: *more existed than you
* received*** (R-4). It does NOT mean "a cap bit" on one tool and "another page
* exists" on another — that is the ambiguity the field report found and R-4
* removed.
*
* **The REMEDY is what `nextCursor` signals, and the pair is the whole design:**
*
* | `truncated` | `nextCursor` | What the agent should do |
* |---|---|---|
* | `false` | `null` | Nothing. You have everything in scope. |
* | `true`  | non-null | Continue the walk with the cursor. |
* | `true`  | `null`/absent | ⛔ **STOP — a server cap you cannot continue past.** Narrow the query (a filter, a smaller window) or accept an incomplete answer. ⚠️ **THREE producers, not one**: the V-1-EXEMPT tools (no cursor at all), `get_app_pods` over its `MAX_PODS` ceiling, and `my_grants` on the last page of a `RESOURCE_GRANT_EXPANSION_CAP`-hit walk. |
* | `false` | non-null | ⛔ **Unreachable by construction.** {@link toPagedResponse} derives one from the other. |
*
* ## ⛔⛔ TWO TIERS, AND THIS DOCBLOCK IS THE ONE WS-2/3/4 READ TO LEARN THE RULE
*
* ⛔ **`nextCursor !== null ⇒ truncated === true` holds EVERYWHERE.** That is the
* one-directional implication, and it is what the helper below guarantees.
*
* ⛔ **The full equivalence `truncated ≡ (nextCursor !== null)` holds ONLY on a
* route with NO cap concept** — pin-history, list-versions, `list_apps`,
* `get_app_history`, `get_app_deployments`. On a route with a hard ceiling the
* equivalence is FALSE by design (R-25, R-32), and there are already **two** such
* routes on this surface: `get_app_pods` (the `MAX_PODS` ceiling) and
* `my_grants` (`RESOURCE_GRANT_EXPANSION_CAP`). On those, the last page of a
* capped walk is `truncated: true` with `nextCursor: null` — rows exist that no
* page will ever carry.
*
* ⚠️ An earlier version of this docblock declared the equivalence universal. It
* is corrected here rather than deleted, because it is the exact
* over-simplification R-25 was minted to withdraw, and this text SHIPS as
* `contracts/src/paging.ts`'s docblock.
*
* The redundancy that remains — emitting a derived flag at all — is deliberate
* surface uniformity (R-4, R-17 E2(b)): an agent reading nine tools should not
* have to learn that some express "there is more" as a null check on a different
* field. An **equivalence-pin test is REQUIRED** on every paged response, in the
* two-tier form §6.0 specifies — ⛔ NOT as a blanket equality, which would fail
* correctly on both capped routes.
*/
/**
* The V-1 response envelope over `itemSchema`.
*
* ⛔ **The items key is PARAMETERIZED** (R-7). New envelopes default to `items`;
* an existing response that already has a domain-meaningful array key KEEPS it
* — `pods` on `get_app_pods`, `grants` on the param-absent `me/grants` shape.
* Renaming a live array key buys vocabulary tidiness and costs every consumer,
* which is a trade this program does not make. ⚠️ The key is a **compile-time
* literal**, not a runtime string, so `PagedResponse` narrows correctly.
*
* ⛔ `.strict()`: an envelope that silently tolerates extra keys is an envelope
* a producer can quietly widen, which is how a response shape and its contract
* stop being the same thing.
*/
function pagedEnvelopeSchema(itemSchema, opts) {
	const key = opts?.key ?? "items";
	return z.object({
		[key]: z.array(itemSchema),
		/** Opaque continuation token, or `null` when the walk is complete. */
		nextCursor: z.string().nullable(),
		/** ⛔ *More existed than you received.* See the table above. */
		truncated: z.boolean()
	}).strict();
}
/**
* ⭐ **THE PEEK-ROW HELPER — every service in this workstream calls it, and none
* of them re-derives it.**
*
* Lifted verbatim in substance from `AppHistoryService.getHistory`
* (`app-history.service.ts:91-100`), which is the house implementation. Three
* things go wrong when this is re-written per endpoint, and all three have
* happened somewhere in this repo's history: an off-by-one that re-emits the
* boundary row, a `nextCursor` minted from the PEEK row instead of the last
* KEPT row (which skips exactly one row per page), and a `truncated` computed
* from `page.length === limit` (which cannot tell "exactly full" from
* "overflowed").
*
* ⛔ **The caller passes `limit + 1` rows.** This function does not issue the
* query and cannot check that; the contract is stated here and asserted in
* §6.1 P-11.
*
* ⛔ `cursorOf` is applied to `page[page.length - 1]` — the last row KEPT, never
* `rows[limit]`, the peek row. The peek row belongs to the NEXT page, so
* encoding it would make the next page start one row late.
*/
function toPagedResponse(rows, limit, cursorOf) {
	const hasMore = rows.length > limit;
	const page = hasMore ? rows.slice(0, limit) : rows;
	const last = page[page.length - 1];
	const nextCursor = hasMore && last !== void 0 ? cursorOf(last) : null;
	return {
		items: page,
		nextCursor,
		truncated: nextCursor !== null
	};
}
/**
* The EXEMPT-tier envelope: a cap you cannot continue past (R-4, row 3).
*
* ⛔ It has **no `nextCursor` field at all** — not `nextCursor: null`. Absence is
* the signal: a tool whose response has no such key cannot be walked, and an
* agent that sees the key set knows that without reading prose. Emitting an
* always-null `nextCursor` would advertise a walk that does not exist.
*/
function cappedEnvelopeSchema(itemSchema, opts) {
	const key = opts?.key ?? "items";
	return z.object({
		[key]: z.array(itemSchema),
		/** ⛔ *More existed than you received*, and there is NO way to continue. */
		truncated: z.boolean()
	}).strict();
}
/**
* Cap a fully-materialised list and REPORT the cap.
*
* ⛔ `truncated` is computed from the FULL list before the slice — never from
* `sliced.length === cap`, which is indistinguishable from "there were exactly
* `cap`". That indistinguishability IS the silent-cap class V-1 exists to kill.
*/
function toCappedResponse(all, cap) {
	return {
		items: all.slice(0, cap),
		truncated: all.length > cap
	};
}
//#endregion
export { InvalidPageCursorError, PAGE_CURSOR_MAX, PAGE_DEFAULT_LIMIT, cappedEnvelopeSchema, decodeKeysetCursor, encodeKeysetCursor, pagedEnvelopeSchema, pagedQueryFields, pagedToolFields, toCappedResponse, toPagedResponse };

//# sourceMappingURL=paging.js.map